import React, { useEffect, useRef, useState } from "react";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { LoginScreen } from "./components/LoginScreen";
import { Header } from "./components/Header";
import { DisclaimerAlert } from "./components/DisclaimerAlert";
import { EditaisSelector } from "./components/EditaisSelector";
import { LoteCard } from "./components/LoteCard";
import { LoteModal } from "./components/LoteModal";
import { AnalyticsPanel } from "./components/AnalyticsPanel";
import { UserManagementModal } from "./components/UserManagementModal";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { fetchWithAuth } from "./services/api";
import { Edital, isEditalAberto, Lote, LoteDetailResponse, ResumoResponse, TipoDisponivel } from "./types";
import {
  Search,
  Filter,
  ArrowUpDown,
  Loader2,
  LayoutGrid,
  List,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Eye,
  Calculator,
  X,
  CheckSquare,
  Square,
  MinusSquare,
} from "lucide-react";

const formatMoney = (val: number | null | undefined) => {
  const num = Number(val);
  if (val === null || val === undefined || isNaN(num)) return "N/D";
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(num);
};

function getPageNumbers(current: number, total: number): (number | string)[] {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const pages: (number | string)[] = [];
  pages.push(1);

  if (current > 3) {
    pages.push("...");
  }

  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);

  for (let i = start; i <= end; i++) {
    pages.push(i);
  }

  if (current < total - 2) {
    pages.push("...");
  }

  pages.push(total);
  return pages;
}

const isCategoriaVeiculo = (tipo: string) => {
  const norm = tipo
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase();
  return (
    norm.includes("VEICUL") ||
    norm.includes("DESMONTAGEM") ||
    norm === "PNEU" ||
    norm.includes("PECA MECANICA")
  );
};

const MainDashboard: React.FC = () => {
  const [editais, setEditais] = useState<Edital[]>([]);
  const [selectedEditalId, setSelectedEditalId] = useState<string | null>(null);
  const [lotes, setLotes] = useState<Lote[]>([]);
  const [resumo, setResumo] = useState<ResumoResponse | null>(null);
  const [selectedLoteDetail, setSelectedLoteDetail] = useState<LoteDetailResponse | null>(null);
  const [modalInitialTab, setModalInitialTab] = useState<"itens" | "markup" | "fotos">("itens");
  const [loadingLoteId, setLoadingLoteId] = useState<string | null>(null);
  const [isEquipeOpen, setIsEquipeOpen] = useState(false);

  // Paginação e Visualização
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalLotes, setTotalLotes] = useState(0);
  const [viewMode, setViewMode] = useState<"grid" | "table">(() => {
    return (localStorage.getItem("rightbid_view_mode") as "grid" | "table") || "grid";
  });

  // Filtros e Ordenação
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [pfOnly, setPfOnly] = useState(false);
  const [sortBy, setSortBy] = useState("custo_unitario_estimado");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  // Filtro de Categorias estilo Excel
  const [tiposDisponiveis, setTiposDisponiveis] = useState<TipoDisponivel[]>([]);
  const [excludedTipos, setExcludedTipos] = useState<string[]>([]);
  const [isCategoryMenuOpen, setIsCategoryMenuOpen] = useState(false);
  const [categorySearch, setCategorySearch] = useState("");
  const categoryMenuRef = useRef<HTMLDivElement | null>(null);

  // Estados de Loading
  const [loadingEditais, setLoadingEditais] = useState(true);
  const [loadingLotes, setLoadingLotes] = useState(false);

  // Fechar menu de categorias ao clicar fora ou pressionar ESC
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (categoryMenuRef.current && !categoryMenuRef.current.contains(e.target as Node)) {
        setIsCategoryMenuOpen(false);
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsCategoryMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  // Debounce na busca para consultas eficientes no servidor
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 350);
    return () => clearTimeout(timer);
  }, [search]);

  // Alterar modo de exibição (Quadro vs Linha/Tabela)
  const handleSetViewMode = (mode: "grid" | "table") => {
    setViewMode(mode);
    localStorage.setItem("rightbid_view_mode", mode);
  };

  // 1. Carregar lista de editais cadastrados
  useEffect(() => {
    async function carregarEditais() {
      try {
        setLoadingEditais(true);
        const res = await fetchWithAuth("/api/editais");
        if (res.ok) {
          const data = await res.json();
          const listaEditais: Edital[] = data.editais || [];
          setEditais(listaEditais);
          if (listaEditais.length > 0) {
            // Prioriza seleção automática de um edital aberto/ativo
            const primeiroAberto = listaEditais.find((e) => isEditalAberto(e.situacao));
            setSelectedEditalId(primeiroAberto ? primeiroAberto.id : listaEditais[0].id);
          }
        }
      } catch (err) {
        console.error("Falha ao carregar editais:", err);
      } finally {
        setLoadingEditais(false);
      }
    }
    carregarEditais();
  }, []);

  // 2. Carregar resumo analítico do edital selecionado
  useEffect(() => {
    if (!selectedEditalId) return;

    async function carregarResumo() {
      try {
        const res = await fetchWithAuth(`/api/resumo/${selectedEditalId}`);
        if (res.ok) {
          const data = await res.json();
          setResumo(data);
        }
      } catch (err) {
        console.error("Erro ao carregar resumo do edital:", err);
      }
    }

    carregarResumo();
  }, [selectedEditalId]);

  // 3. Carregar lotes paginados (máximo 50 itens) com busca e filtros no servidor
  useEffect(() => {
    if (!selectedEditalId) return;

    async function carregarLotes() {
      setLoadingLotes(true);
      try {
        const params = new URLSearchParams({
          page: String(page),
          limit: "50",
          sort: sortBy,
          order: sortOrder,
          ...(pfOnly ? { pf_only: "true" } : {}),
          ...(debouncedSearch.trim() ? { search: debouncedSearch.trim() } : {}),
        });

        const allTipos = tiposDisponiveis.map((t) => t.tipo);
        if (excludedTipos.length > 0) {
          if (allTipos.length > 0 && excludedTipos.length >= allTipos.length) {
            params.set("tipos_in", "__NONE__");
          } else if (allTipos.length > 0 && excludedTipos.length > allTipos.length / 2) {
            const incluidos = allTipos.filter((t) => !excludedTipos.includes(t));
            params.set("tipos_in", incluidos.join(","));
          } else {
            params.set("tipos_out", excludedTipos.join(","));
          }
        }

        const resLotes = await fetchWithAuth(`/api/lotes/edital/${selectedEditalId}?${params}`);
        if (resLotes.ok) {
          const dataLotes = await resLotes.json();
          setLotes(dataLotes.lotes || []);
          setTotalPages(dataLotes.totalPages || 1);
          setTotalLotes(dataLotes.total || 0);
          if (Array.isArray(dataLotes.tiposDisponiveis)) {
            setTiposDisponiveis(dataLotes.tiposDisponiveis);
          }
        }
      } catch (err) {
        console.error("Erro ao carregar lotes do edital:", err);
      } finally {
        setLoadingLotes(false);
      }
    }

    carregarLotes();
  }, [selectedEditalId, page, sortBy, sortOrder, pfOnly, debouncedSearch, excludedTipos]);

  // Funções auxiliares do Filtro de Categorias estilo Excel
  const allTiposNames = tiposDisponiveis.map((t) => t.tipo);
  const filteredTiposMenu = tiposDisponiveis.filter((t) =>
    t.tipo.toLowerCase().includes(categorySearch.trim().toLowerCase())
  );
  const visibleTiposNames = filteredTiposMenu.map((t) => t.tipo);
  const selectedCount = Math.max(0, allTiposNames.length - excludedTipos.length);
  const isAllVisibleChecked =
    visibleTiposNames.length > 0 && visibleTiposNames.every((t) => !excludedTipos.includes(t));
  const isSomeVisibleChecked =
    visibleTiposNames.some((t) => !excludedTipos.includes(t)) && !isAllVisibleChecked;

  const veiculosTipos = allTiposNames.filter(isCategoriaVeiculo);
  const isExcetoVeiculosActive =
    veiculosTipos.length > 0 &&
    veiculosTipos.every((v) => excludedTipos.includes(v)) &&
    excludedTipos.length === veiculosTipos.length;

  const handleToggleCategoria = (tipo: string) => {
    setExcludedTipos((prev) =>
      prev.includes(tipo) ? prev.filter((t) => t !== tipo) : [...prev, tipo]
    );
    setPage(1);
  };

  const handleSelectOnlyCategoria = (tipo: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExcludedTipos(allTiposNames.filter((t) => t !== tipo));
    setPage(1);
  };

  const handleToggleAllVisible = () => {
    if (isAllVisibleChecked) {
      // Desmarcar todos os visíveis
      setExcludedTipos((prev) => Array.from(new Set([...prev, ...visibleTiposNames])));
    } else {
      // Marcar todos os visíveis
      setExcludedTipos((prev) => prev.filter((t) => !visibleTiposNames.includes(t)));
    }
    setPage(1);
  };

  const handleToggleExcetoVeiculos = () => {
    if (isExcetoVeiculosActive) {
      setExcludedTipos([]);
    } else {
      setExcludedTipos(veiculosTipos);
    }
    setPage(1);
  };

  // 4. Abrir modal com detalhamento do lote
  const handleOpenLote = async (
    loteId: string,
    initialTab: "itens" | "markup" | "fotos" = "itens"
  ) => {
    try {
      setLoadingLoteId(loteId);
      setModalInitialTab(initialTab);
      const res = await fetchWithAuth(`/api/lotes/${loteId}`);
      if (res.ok) {
        const data = await res.json();
        setSelectedLoteDetail(data);
      }
    } catch (err) {
      console.error("Erro ao abrir detalhe do lote:", err);
    } finally {
      setLoadingLoteId(null);
    }
  };

  return (
    <div className="app-container">
      {/* Header com identidade, logout e gestão de equipe */}
      <Header
        editaisCount={editais.length}
        onOpenEquipe={() => setIsEquipeOpen(true)}
      />
      <DisclaimerAlert />

      {/* Seletor de Editais */}
      {loadingEditais ? (
        <div style={{ display: "flex", justifyContent: "center", padding: "40px" }}>
          <Loader2 className="animate-spin" size={32} color="#3b82f6" />
        </div>
      ) : editais.length > 0 ? (
        <EditaisSelector
          editais={editais}
          selectedEditalId={selectedEditalId}
          onSelectEdital={(id) => {
            setSelectedEditalId(id);
            setPage(1);
            setSearch("");
            setDebouncedSearch("");
            setExcludedTipos([]);
            setCategorySearch("");
          }}
        />
      ) : (
        <div style={{ textAlign: "center", padding: "30px", background: "var(--bg-secondary)", borderRadius: "12px" }}>
          Nenhum edital cadastrado no momento. Execute o pipeline ETL para sincronizar.
        </div>
      )}

      {/* Painel Analítico de Inteligência do Edital */}
      <AnalyticsPanel resumo={resumo} />

      {/* Barra de Controles, Filtros e Alternância de Visualização */}
      <div className="controls-bar">
        {/* Campo de Busca Global */}
        <div className="search-input-wrapper">
          <Search size={18} color="var(--text-muted)" style={{ position: "absolute", left: "12px", top: "12px" }} />
          <input
            type="text"
            className="search-input"
            placeholder="Buscar por lote (ex: 150), tipo, cidade ou mercadoria..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div style={{ display: "flex", gap: "12px", alignItems: "center", flexWrap: "wrap" }}>
          {/* Alternância entre Quadro (Cards) e Linha (Tabela) */}
          <div className="view-mode-toggle">
            <button
              type="button"
              className={`view-mode-btn ${viewMode === "grid" ? "active" : ""}`}
              onClick={() => handleSetViewMode("grid")}
              title="Visualização em Quadro (Cards detalhados com 3 por linha)"
            >
              <LayoutGrid size={15} /> Quadro
            </button>
            <button
              type="button"
              className={`view-mode-btn ${viewMode === "table" ? "active" : ""}`}
              onClick={() => handleSetViewMode("table")}
              title="Visualização em Linha (Formato tabela sintética sem prévia de itens)"
            >
              <List size={15} /> Linha
            </button>
          </div>

          {/* Filtro de Categorias estilo Excel (Marcar / Desmarcar / Exceto Veículos) */}
          <div className="category-filter-wrapper" ref={categoryMenuRef}>
            <button
              type="button"
              className={`btn ${excludedTipos.length > 0 ? "btn-primary" : "btn-outline"}`}
              onClick={() => setIsCategoryMenuOpen((open) => !open)}
              title="Filtrar ou ocultar categorias de lotes (estilo filtro do Excel)"
            >
              <Filter size={16} />
              <span>
                {excludedTipos.length === 0
                  ? `Categorias (${allTiposNames.length || "Todas"})`
                  : isExcetoVeiculosActive
                  ? "Categorias: Exceto Veículos"
                  : `Categorias (${selectedCount}/${allTiposNames.length})`}
              </span>
              <ChevronDown size={15} />
            </button>

            {isCategoryMenuOpen && (
              <div className="category-filter-popover">
                <div className="category-filter-header">
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontSize: "0.82rem", fontWeight: 700, color: "var(--text-main)" }}>
                      Filtrar por Categoria do Lote
                    </span>
                    {excludedTipos.length > 0 && (
                      <button
                        type="button"
                        onClick={() => {
                          setExcludedTipos([]);
                          setPage(1);
                        }}
                        style={{
                          background: "transparent",
                          border: "none",
                          color: "#60a5fa",
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          cursor: "pointer",
                        }}
                      >
                        Restaurar todas
                      </button>
                    )}
                  </div>

                  {/* Busca rápida de categoria dentro do filtro */}
                  <div style={{ position: "relative" }}>
                    <Search
                      size={14}
                      color="var(--text-muted)"
                      style={{ position: "absolute", left: "10px", top: "9px" }}
                    />
                    <input
                      type="text"
                      className="category-filter-search"
                      placeholder="Pesquisar categoria na lista..."
                      value={categorySearch}
                      onChange={(e) => setCategorySearch(e.target.value)}
                    />
                  </div>

                  {/* Atalhos rápidos estilo Excel */}
                  <div className="category-filter-actions">
                    <button
                      type="button"
                      onClick={handleToggleAllVisible}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        background: "transparent",
                        border: "none",
                        color: "var(--text-main)",
                        fontSize: "0.8rem",
                        fontWeight: 600,
                        cursor: "pointer",
                      }}
                    >
                      {isAllVisibleChecked ? (
                        <CheckSquare size={16} color="#3b82f6" />
                      ) : isSomeVisibleChecked ? (
                        <MinusSquare size={16} color="#60a5fa" />
                      ) : (
                        <Square size={16} color="var(--text-muted)" />
                      )}
                      <span>
                        {categorySearch.trim() ? "(Selecionar Busca)" : "(Selecionar Tudo)"}
                      </span>
                    </button>

                    {veiculosTipos.length > 0 && (
                      <button
                        type="button"
                        className={`category-quick-btn ${isExcetoVeiculosActive ? "active-exclude" : ""}`}
                        onClick={handleToggleExcetoVeiculos}
                        title="Desmarca automaticamente todas as categorias de veículos, sucata/desmontagem e peças automotivas"
                      >
                        {isExcetoVeiculosActive ? "✓ Exceto Veículos" : "🚫 Exceto Veículos"}
                      </button>
                    )}
                  </div>
                </div>

                {/* Lista de categorias com checkboxes e contadores */}
                <div className="category-filter-list">
                  {filteredTiposMenu.length > 0 ? (
                    filteredTiposMenu.map((item) => {
                      const checked = !excludedTipos.includes(item.tipo);
                      return (
                        <div
                          key={item.tipo}
                          className={`category-filter-item ${!checked ? "unchecked" : ""}`}
                          onClick={() => handleToggleCategoria(item.tipo)}
                        >
                          <div style={{ display: "flex", alignItems: "center", gap: "8px", minWidth: 0 }}>
                            {checked ? (
                              <CheckSquare size={16} color="#3b82f6" style={{ flexShrink: 0 }} />
                            ) : (
                              <Square size={16} color="var(--text-dim)" style={{ flexShrink: 0 }} />
                            )}
                            <span
                              style={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                fontWeight: checked ? 600 : 400,
                              }}
                              title={item.tipo}
                            >
                              {item.tipo}
                            </span>
                          </div>

                          <div style={{ display: "flex", alignItems: "center", gap: "6px", flexShrink: 0 }}>
                            <button
                              type="button"
                              className="category-only-btn"
                              onClick={(e) => handleSelectOnlyCategoria(item.tipo, e)}
                              title={`Mostrar somente a categoria ${item.tipo}`}
                            >
                              Somente
                            </button>
                            <span
                              style={{
                                fontSize: "0.72rem",
                                fontFamily: "var(--font-mono)",
                                color: checked ? "var(--text-muted)" : "var(--text-dim)",
                                background: "rgba(17, 24, 39, 0.7)",
                                padding: "2px 7px",
                                borderRadius: "999px",
                              }}
                            >
                              {item.total_lotes}
                            </span>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div style={{ padding: "18px", textAlign: "center", color: "var(--text-dim)", fontSize: "0.8rem" }}>
                      Nenhuma categoria encontrada para &quot;{categorySearch}&quot;.
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Filtro PF */}
          <button
            className={`btn ${pfOnly ? "btn-primary" : "btn-outline"}`}
            onClick={() => {
              setPfOnly(!pfOnly);
              setPage(1);
            }}
          >
            <Filter size={16} /> Apenas Pessoa Física (PF)
          </button>

          {/* Ordenação */}
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <ArrowUpDown size={16} color="var(--text-muted)" />
            <select
              className="select-custom"
              value={sortBy}
              onChange={(e) => {
                setSortBy(e.target.value);
                setPage(1);
              }}
            >
              <option value="nr_atribuido">Número do Lote</option>
              <option value="custo_unitario_estimado">Menor Custo Unitário Estimado</option>
              <option value="valor_minimo">Menor Lance Mínimo</option>
              <option value="qtde_total_unidades">Maior Quantidade de Unidades</option>
            </select>

            <select
              className="select-custom"
              value={sortOrder}
              onChange={(e) => {
                setSortOrder(e.target.value as "asc" | "desc");
                setPage(1);
              }}
            >
              <option value="asc">Crescente (Menor primeiro)</option>
              <option value="desc">Decrescente (Maior primeiro)</option>
            </select>
          </div>
        </div>
      </div>

      {/* Barra de Chips de Filtros de Categoria Ativos (permite remover item a item ou limpar tudo) */}
      {excludedTipos.length > 0 && (
        <div className="active-filters-bar">
          {selectedCount === 0 ? (
            <span style={{ color: "#fda4af", fontWeight: 600 }}>
              Todas as categorias estão desmarcadas.
            </span>
          ) : excludedTipos.length <= Math.ceil(allTiposNames.length / 2) ? (
            <>
              <span style={{ color: "var(--text-muted)", fontWeight: 600 }}>
                Ocultando categorias (clique para incluir novamente):
              </span>
              {excludedTipos.map((tipo) => (
                <button
                  key={`excl-${tipo}`}
                  type="button"
                  className="filter-chip filter-chip-excluded"
                  onClick={() => handleToggleCategoria(tipo)}
                  title={`Clique para voltar a exibir lotes de ${tipo}`}
                >
                  <span>🚫 {tipo}</span>
                  <X size={13} />
                </button>
              ))}
            </>
          ) : (
            <>
              <span style={{ color: "var(--text-muted)", fontWeight: 600 }}>
                Exibindo apenas categorias (clique para remover do filtro):
              </span>
              {allTiposNames
                .filter((t) => !excludedTipos.includes(t))
                .map((tipo) => (
                  <button
                    key={`incl-${tipo}`}
                    type="button"
                    className="filter-chip filter-chip-included"
                    onClick={() => handleToggleCategoria(tipo)}
                    title={`Clique para desmarcar ${tipo}`}
                  >
                    <span>✓ {tipo}</span>
                    <X size={13} />
                  </button>
                ))}
            </>
          )}

          <button
            type="button"
            onClick={() => {
              setExcludedTipos([]);
              setPage(1);
            }}
            style={{
              marginLeft: "auto",
              background: "transparent",
              border: "1px solid var(--border-color)",
              color: "var(--text-main)",
              padding: "4px 10px",
              borderRadius: "8px",
              fontSize: "0.75rem",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Limpar filtro de categorias
          </button>
        </div>
      )}

      {/* Barra de Status e Paginação Superior */}
      <div className="pagination-container">
        <div style={{ fontSize: "0.875rem", color: "var(--text-muted)" }}>
          {loadingLotes ? (
            <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Loader2 className="animate-spin" size={16} color="#3b82f6" />
              Carregando lotes...
            </span>
          ) : (
            <>
              Exibindo <strong style={{ color: "var(--text-main)" }}>{lotes.length}</strong> de{" "}
              <strong style={{ color: "var(--text-main)" }}>{totalLotes}</strong> lotes
              {debouncedSearch && <span> para a busca &quot;{debouncedSearch}&quot;</span>}
              {totalPages > 1 && (
                <> • Página <strong style={{ color: "#60a5fa" }}>{page}</strong> de <strong style={{ color: "#60a5fa" }}>{totalPages}</strong> (máx. 50 por página)</>
              )}
            </>
          )}
        </div>

        {totalPages > 1 && (
          <div className="pagination-pages">
            <button
              type="button"
              className="page-btn"
              disabled={page <= 1 || loadingLotes}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              title="Página anterior"
            >
              <ChevronLeft size={16} /> Anterior
            </button>

            {getPageNumbers(page, totalPages).map((p, idx) =>
              p === "..." ? (
                <span key={`dots-top-${idx}`} style={{ padding: "0 4px", color: "var(--text-dim)" }}>
                  ...
                </span>
              ) : (
                <button
                  key={`top-page-${p}`}
                  type="button"
                  className={`page-btn ${page === p ? "active" : ""}`}
                  disabled={loadingLotes}
                  onClick={() => setPage(Number(p))}
                >
                  {p}
                </button>
              )
            )}

            <button
              type="button"
              className="page-btn"
              disabled={page >= totalPages || loadingLotes}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              title="Próxima página"
            >
              Próxima <ChevronRight size={16} />
            </button>
          </div>
        )}
      </div>

      {/* Área Principal de Exibição de Lotes */}
      {loadingLotes ? (
        <div style={{ display: "flex", justifyContent: "center", padding: "60px" }}>
          <Loader2 className="animate-spin" size={36} color="#3b82f6" />
        </div>
      ) : lotes.length > 0 ? (
        viewMode === "grid" ? (
          /* MODO QUADRO: 3 por linha com cards completos e prévia de itens */
          <main className="lotes-grid">
            {lotes.map((lote) => (
              <div key={lote.id} style={{ position: "relative" }}>
                <LoteCard
                  lote={lote}
                  onClick={() => handleOpenLote(lote.id, "itens")}
                  onSimulate={() => handleOpenLote(lote.id, "markup")}
                />
                {loadingLoteId === lote.id && (
                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      background: "rgba(11, 15, 25, 0.7)",
                      borderRadius: "16px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      zIndex: 10,
                    }}
                  >
                    <Loader2 className="animate-spin" size={28} color="#38bdf8" />
                  </div>
                )}
              </div>
            ))}
          </main>
        ) : (
          /* MODO LINHA: Formato tabela sintética sem exibição de itens que compõem o lote */
          <div className="table-responsive">
            <table className="lotes-table">
              <thead>
                <tr>
                  <th style={{ minWidth: "100px" }}>Lote</th>
                  <th>Tipo / Categoria</th>
                  <th>Cidade / Recinto</th>
                  <th>Permissão</th>
                  <th style={{ textAlign: "right" }}>Lance Mínimo</th>
                  <th style={{ textAlign: "right" }}>Avaliação RFB</th>
                  <th style={{ textAlign: "center" }}>Unidades Físicas</th>
                  <th style={{ textAlign: "right" }}>Custo Unitário Médio</th>
                  <th style={{ textAlign: "center", minWidth: "160px" }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {lotes.map((lote) => (
                  <tr
                    key={lote.id}
                    onClick={() => handleOpenLote(lote.id, "itens")}
                    title={`Clique para ver o lote #${lote.nr_atribuido}`}
                  >
                    <td style={{ fontWeight: 800, color: "var(--primary)", fontSize: "0.95rem" }}>
                      #{lote.nr_atribuido}
                      {lote.lote_heterogeneo === 1 && (
                        <span
                          className="badge badge-heterogeneo"
                          style={{ marginLeft: "8px", fontSize: "0.65rem", padding: "2px 6px" }}
                          title="Lote heterogêneo com múltiplos produtos distintos"
                        >
                          Misto
                        </span>
                      )}
                    </td>
                    <td>
                      <span className="badge badge-ativo" style={{ fontSize: "0.75rem" }}>
                        {lote.tipo || "DIVERSOS"}
                      </span>
                    </td>
                    <td style={{ color: "var(--text-muted)" }}>
                      {lote.cidade || "—"}
                    </td>
                    <td>
                      {lote.permite_pf === 1 ? (
                        <span className="badge badge-pf" style={{ fontSize: "0.7rem", padding: "2px 8px" }}>
                          PF & PJ
                        </span>
                      ) : (
                        <span className="badge badge-pj" style={{ fontSize: "0.7rem", padding: "2px 8px" }}>
                          Apenas PJ
                        </span>
                      )}
                    </td>
                    <td style={{ textAlign: "right", fontWeight: 700, color: "#34d399", fontFamily: "var(--font-mono)" }}>
                      {formatMoney(lote.valor_minimo)}
                    </td>
                    <td style={{ textAlign: "right", color: lote.valor_avaliacao ? "#93c5fd" : "var(--text-dim)", fontFamily: "var(--font-mono)" }}>
                      {lote.valor_avaliacao ? formatMoney(lote.valor_avaliacao) : "—"}
                    </td>
                    <td style={{ textAlign: "center" }}>
                      <span style={{ fontWeight: 600 }}>{lote.qtde_total_unidades.toLocaleString("pt-BR")} un</span>
                      <span style={{ fontSize: "0.75rem", color: "var(--text-dim)", display: "block" }}>
                        ({lote.qtde_itens_distintos} distintos)
                      </span>
                    </td>
                    <td style={{ textAlign: "right", fontWeight: 600, color: "var(--text-main)", fontFamily: "var(--font-mono)" }}>
                      {lote.custo_unitario_estimado !== null ? `${formatMoney(lote.custo_unitario_estimado)}/un` : "—"}
                    </td>
                    <td style={{ textAlign: "center" }} onClick={(e) => e.stopPropagation()}>
                      <div style={{ display: "flex", gap: "6px", justifyContent: "center" }}>
                        <button
                          type="button"
                          className="btn btn-outline"
                          onClick={() => handleOpenLote(lote.id, "itens")}
                          style={{ padding: "6px 10px", fontSize: "0.75rem" }}
                          title="Ver composição de mercadorias"
                        >
                          <Eye size={14} /> Itens
                        </button>
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={() => handleOpenLote(lote.id, "markup")}
                          style={{ padding: "6px 10px", fontSize: "0.75rem" }}
                          title="Abrir Simulador de Lance & Markup"
                        >
                          <Calculator size={14} /> Simular
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : (
        <div style={{ textAlign: "center", padding: "60px", color: "var(--text-muted)", background: "var(--bg-secondary)", borderRadius: "16px" }}>
          Nenhum lote corresponde aos critérios de busca ou filtro selecionados.
        </div>
      )}

      {/* Barra de Paginação Inferior */}
      {!loadingLotes && totalPages > 1 && (
        <div className="pagination-container" style={{ marginTop: "12px" }}>
          <div style={{ fontSize: "0.85rem", color: "var(--text-muted)" }}>
            Página <strong style={{ color: "#60a5fa" }}>{page}</strong> de <strong style={{ color: "#60a5fa" }}>{totalPages}</strong> • Total de <strong style={{ color: "var(--text-main)" }}>{totalLotes}</strong> lotes
          </div>

          <div className="pagination-pages">
            <button
              type="button"
              className="page-btn"
              disabled={page <= 1 || loadingLotes}
              onClick={() => {
                setPage((p) => Math.max(1, p - 1));
                window.scrollTo({ top: 350, behavior: "smooth" });
              }}
              title="Página anterior"
            >
              <ChevronLeft size={16} /> Anterior
            </button>

            {getPageNumbers(page, totalPages).map((p, idx) =>
              p === "..." ? (
                <span key={`dots-bot-${idx}`} style={{ padding: "0 4px", color: "var(--text-dim)" }}>
                  ...
                </span>
              ) : (
                <button
                  key={`bot-page-${p}`}
                  type="button"
                  className={`page-btn ${page === p ? "active" : ""}`}
                  disabled={loadingLotes}
                  onClick={() => {
                    setPage(Number(p));
                    window.scrollTo({ top: 350, behavior: "smooth" });
                  }}
                >
                  {p}
                </button>
              )
            )}

            <button
              type="button"
              className="page-btn"
              disabled={page >= totalPages || loadingLotes}
              onClick={() => {
                setPage((p) => Math.min(totalPages, p + 1));
                window.scrollTo({ top: 350, behavior: "smooth" });
              }}
              title="Próxima página"
            >
              Próxima <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Modal de Detalhes do Lote com Calculadora de Markup e Navegação entre Lotes */}
      {selectedLoteDetail && (
        <ErrorBoundary
          fallbackTitle="Instabilidade ao carregar detalhes ou simulação do lote"
          onReset={() => setSelectedLoteDetail(null)}
        >
          <LoteModal
            detail={selectedLoteDetail}
            initialTab={modalInitialTab}
            onClose={() => setSelectedLoteDetail(null)}
            onNavigate={(nextId, currentTab) => handleOpenLote(nextId, currentTab)}
            isLoadingAdjacent={loadingLoteId !== null}
          />
        </ErrorBoundary>
      )}

      {/* Modal de Gestão de Equipe (Apenas para ADMIN) */}
      <UserManagementModal
        isOpen={isEquipeOpen}
        onClose={() => setIsEquipeOpen(false)}
      />
    </div>
  );
};

const AuthGate: React.FC = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          background: "#0b0f19",
          color: "#f8fafc",
          gap: "16px",
        }}
      >
        <Loader2 size={36} className="animate-spin" color="#3b82f6" />
        <span style={{ fontSize: "0.9rem", color: "var(--text-muted)" }}>
          Verificando credenciais seguras...
        </span>
      </div>
    );
  }

  if (!user) {
    return <LoginScreen />;
  }

  return (
    <ErrorBoundary fallbackTitle="Ocorreu um erro inesperado no Right Bid">
      <MainDashboard />
    </ErrorBoundary>
  );
};

export const App: React.FC = () => {
  return (
    <ErrorBoundary fallbackTitle="Falha crítica no aplicativo">
      <AuthProvider>
        <AuthGate />
      </AuthProvider>
    </ErrorBoundary>
  );
};
export default App;
